import { ConversationalBtmSseRecord } from './conversational-btm-events';

/** Incremental SSE parser that preserves opaque event ids and arbitrary byte boundaries. */
export class ConversationalBtmSseParser {
  private buffer = '';
  private dataLines: string[] = [];
  private eventName?: string;
  private eventId?: string;
  private retry?: number;

  push(chunk: string): ConversationalBtmSseRecord[] {
    this.buffer += chunk;
    return this.drainLines(false);
  }

  finish(): ConversationalBtmSseRecord[] {
    const records = this.drainLines(true);
    const finalRecord = this.dispatch();
    return finalRecord ? [...records, finalRecord] : records;
  }

  private drainLines(flush: boolean): ConversationalBtmSseRecord[] {
    const records: ConversationalBtmSseRecord[] = [];

    while (this.buffer.length > 0) {
      const newline = this.findNewline();
      if (!newline) {
        if (flush) {
          this.consumeLine(this.buffer, records);
          this.buffer = '';
        }
        break;
      }
      if (newline.length === 0 && !flush) break;

      const line = this.buffer.slice(0, newline.index);
      const newlineLength = newline.length || 1;
      this.buffer = this.buffer.slice(newline.index + newlineLength);
      this.consumeLine(line, records);
    }

    return records;
  }

  private findNewline(): { index: number; length: number } | null {
    for (let index = 0; index < this.buffer.length; index += 1) {
      const character = this.buffer[index];
      if (character === '\n') return { index, length: 1 };
      if (character === '\r') {
        if (index === this.buffer.length - 1) {
          return { index, length: 0 };
        }
        return {
          index,
          length: this.buffer[index + 1] === '\n' ? 2 : 1,
        };
      }
    }
    return null;
  }

  private consumeLine(
    line: string,
    records: ConversationalBtmSseRecord[]
  ): void {
    if (line === '') {
      const record = this.dispatch();
      if (record) records.push(record);
      return;
    }
    if (line.startsWith(':')) return;

    const separator = line.indexOf(':');
    const field = separator >= 0 ? line.slice(0, separator) : line;
    let value = separator >= 0 ? line.slice(separator + 1) : '';
    if (value.startsWith(' ')) value = value.slice(1);

    switch (field) {
      case 'data':
        this.dataLines.push(value);
        break;
      case 'event':
        this.eventName = value || undefined;
        break;
      case 'id':
        if (!value.includes('\0')) this.eventId = value;
        break;
      case 'retry': {
        const parsed = Number(value);
        if (Number.isInteger(parsed) && parsed >= 0) this.retry = parsed;
        break;
      }
    }
  }

  private dispatch(): ConversationalBtmSseRecord | null {
    if (this.dataLines.length === 0) {
      this.eventName = undefined;
      this.eventId = undefined;
      this.retry = undefined;
      return null;
    }

    const record: ConversationalBtmSseRecord = {
      data: this.dataLines.join('\n'),
      ...(this.eventId !== undefined ? { id: this.eventId } : {}),
      ...(this.eventName ? { event: this.eventName } : {}),
      ...(this.retry !== undefined ? { retry: this.retry } : {}),
    };
    this.dataLines = [];
    this.eventName = undefined;
    this.eventId = undefined;
    this.retry = undefined;
    return record;
  }
}
